<template>
  <div id="email_analytics_grid">
    <InfoGrid :columns="3">
      <ContainerInfoItem
        v-for="(data, key) in analyticsData"
        :key="key"
        source="sequence_email_activity_analytics"
        :title="data.title"
        :description="range?.label || ''"
        :value="formatAnalyticsValue(data.value, data.unit)"
        :is-loading="data.isLoading"
      />
    </InfoGrid>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from "vue";
import { formatAnalyticsValue } from "@/src/utils/analyticUtils.js";
import store from "@/src/store/index.js";
import AnalyticService from "@/src/api/AnalyticService.js";
import InfoGrid from "@/src/components/info_components/InfoGrid.vue";
import ContainerInfoItem from "@/src/components/info_components/ContainerInfoItem.vue";
import { useAnalyticsRefresh } from "@/src/composables/useAnalyticsRefresh.js";
import { SequenceEmailAnalyticsData, sequenceActions, units } from "@/src/constants/analyticConstants.js";

const range = computed(() => store.getters["analytics/getRange"]);
const filters = computed(() => store.getters["analytics/getFilters"]);
const userIds = computed(() => store.getters["analytics/getUserIds"]);
const teamIds = computed(() => store.getters["analytics/getTeamIds"]);

const rateMetrics = [
  {
    id: sequenceActions.EMAIL_OPEN_RATE,
    title: "Open rate",
    value: 0,
    isLoading: false,
    unit: units.RATE,
  },
  {
    id: sequenceActions.EMAIL_CLICK_RATE,
    title: "Click rate",
    value: 0,
    isLoading: false,
    unit: units.RATE,
  },
  {
    id: sequenceActions.EMAIL_REPLY_RATE,
    title: "Reply rate",
    value: 0,
    isLoading: false,
    unit: units.RATE,
  },
];

const analyticsData = reactive([
  ...SequenceEmailAnalyticsData.map((item) => ({ ...item })),
  ...rateMetrics,
]);

const fetchAnalytics = async () => {
  analyticsData.forEach((item) => {
    item.isLoading = true;
  });

  try {
    const params = {
      user_id: userIds.value,
      team_id: teamIds.value,
      start_time: range.value?.start,
      end_time: range.value?.end,
      sequence_id: filters.value?.sequence?.id,
    };

    AnalyticService.V2.fetchSequenceEmailCount(params).then((response) => {
      if (response?.success) {
        const data = response?.data?.results;
        analyticsData.forEach((item) => {
          const action = item.id;
          item.value = data?.[action] || 0;
        });
      }
    });

    AnalyticService.V2.fetchSequenceRateMetrics(params).then((response) => {
      if (response && response.data) {
        const emailRates = response.data.email_rates || {};
        analyticsData.forEach((item) => {
          if (emailRates[item.id] !== undefined) {
            item.value = emailRates[item.id];
          }
        });
      }
    });

  } catch (error) {
    console.error("An error occurred while fetching analytics: ", error);
  } finally {
    analyticsData.forEach((item) => {
      item.isLoading = false;
    });
  }
};

watch(
  [range, filters, userIds, teamIds],
  () => {
    if (range.value || filters.value?.sequence?.id) {
      fetchAnalytics();
    }
  },
  { deep: true }
);

useAnalyticsRefresh(fetchAnalytics, [range, userIds, teamIds]);
</script>
